import { useRef, useState } from "react";
import { X, Trash, MapPin, Circle, Check } from "@phosphor-icons/react";
import { photoUrl } from "./walkApi";

/**
 * Photo call-outs: tap the photo to drop a numbered pin (or drag for a circle),
 * add a short note per pin. Saved on the photo, burned into the PDF.
 */
export default function CalloutEditor({ photo, onSave, onClose, onDelete }) {
  const [pins, setPins] = useState((photo.callouts || []).map(c => ({ ...c })));
  const [tool, setTool] = useState("pin");   // pin | circle
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const drag = useRef(null);

  const frac = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return { x: Math.min(1, Math.max(0, (pt.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (pt.clientY - r.top) / r.height)) };
  };
  const down = (e) => { drag.current = frac(e); };
  const up = (e) => {
    if (!drag.current) return;
    const a = drag.current; const b = e.changedTouches ? frac({ clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY }) : frac(e);
    drag.current = null;
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    const r = tool === "circle" ? Math.max(0.04, d) : 0;
    const n = pins.length + 1;
    setPins(p => [...p, { n, x: a.x, y: a.y, r, note: "" }]);
    setActive(n);
  };
  const removePin = (n) => { setPins(p => p.filter(c => c.n !== n).map((c, i) => ({ ...c, n: i + 1 }))); setActive(null); };
  const save = async () => { setSaving(true); try { await onSave(pins); onClose(); } finally { setSaving(false); } };
  const act = pins.find(p => p.n === active);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col text-white" data-testid="callout-editor">
      <div className="flex items-center justify-between px-4 py-3 bg-black/70">
        <div className="text-sm text-slate-300">{pins.length ? `${pins.length} call-out${pins.length === 1 ? "" : "s"}` : "Tap the photo to drop a pin"}</div>
        <div className="flex gap-2">
          <button onClick={() => setTool("pin")} className={`h-9 px-3 rounded-full text-xs font-black flex items-center gap-1 ${tool === "pin" ? "bg-teal-500 text-slate-900" : "bg-white/15"}`} data-testid="callout-tool-pin"><MapPin weight="fill" size={14}/> Pin</button>
          <button onClick={() => setTool("circle")} className={`h-9 px-3 rounded-full text-xs font-black flex items-center gap-1 ${tool === "circle" ? "bg-teal-500 text-slate-900" : "bg-white/15"}`} data-testid="callout-tool-circle"><Circle weight="bold" size={14}/> Circle</button>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="callout-close"><X size={18}/></button>
        </div>
      </div>
      <div className="flex-1 relative flex items-center justify-center min-h-0 select-none">
        <div className="relative inline-block max-h-full">
          <img ref={imgRef} src={photoUrl(photo)} alt="" className="max-h-[60vh] max-w-full object-contain touch-none" draggable={false}
            onMouseDown={down} onMouseUp={up} onTouchStart={down} onTouchEnd={up} data-testid="callout-image"/>
          {pins.map(c => (
            <div key={c.n} className="absolute pointer-events-none" style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}>
              {c.r > 0 && <div className="absolute rounded-full border-[3px] border-red-500" style={{ width: `${c.r * 2 * (imgRef.current?.clientWidth || 300)}px`, height: `${c.r * 2 * (imgRef.current?.clientWidth || 300)}px`, left: `-${c.r * (imgRef.current?.clientWidth || 300)}px`, top: `-${c.r * (imgRef.current?.clientWidth || 300)}px` }}/>}
              <button onClick={() => setActive(c.n)} className={`pointer-events-auto -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-2 border-white text-sm font-black flex items-center justify-center shadow ${active === c.n ? "bg-teal-500 text-slate-900" : "bg-red-600"}`} data-testid={`callout-pin-${c.n}`}>{c.n}</button>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-[#0b1c2d] p-4 space-y-3 pb-8">
        {act ? (
          <div className="flex gap-2 items-center" data-testid="callout-note-row">
            <span className="w-8 h-8 rounded-full bg-red-600 font-black flex items-center justify-center shrink-0">{act.n}</span>
            <input autoFocus value={act.note} onChange={e => setPins(p => p.map(c => c.n === act.n ? { ...c, note: e.target.value } : c))} placeholder="What's here? e.g. hole in drywall, 3in"
              className="flex-1 h-11 px-3 rounded-xl bg-white/10 border border-white/15 text-base placeholder-slate-500" data-testid="callout-note"/>
            <button onClick={() => removePin(act.n)} className="w-11 h-11 rounded-xl bg-red-600/80 flex items-center justify-center" data-testid="callout-remove"><Trash weight="fill" size={18}/></button>
          </div>
        ) : (
          <div className="text-xs text-slate-400">{tool === "circle" ? "Press and drag on the photo to circle an area." : "Tap a spot on the photo to number it, then add a note."} Tap a number to edit.</div>
        )}
        <div className="flex gap-2">
          {onDelete && <button onClick={onDelete} className="h-12 px-4 rounded-2xl bg-white/10 font-bold text-sm">Delete photo</button>}
          <button onClick={save} disabled={saving} data-testid="callout-save" className="flex-1 h-12 rounded-2xl bg-teal-500 text-slate-900 font-black flex items-center justify-center gap-2"><Check weight="bold" size={20}/> Save call-outs</button>
        </div>
      </div>
    </div>
  );
}
